import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import starWhite from 'vectors/star_white.svg';
import { Text } from 'Text';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    The saved state is slate grey, not blue. The legacy dashboard repainted the "dark-blue" class.
    The ribbon uses a clip-path so its colour follows the state. The legacy pseudo-element tail kept the blue.
    The title is 24px, inherited from the button, not the 14px on .bookmark-button.
*/
const RIBBON_SHAPE = '[clip-path:polygon(0_0,100%_0,100%_100%,50%_72%,0_100%)]';

type Props = {
    favorite: boolean;

    // Signed-out visitors see the button, but it does nothing for them.
    disabled: boolean;
    onToggle: () => void;
    className?: string;
};

export const BookmarkButton: FC<Props> = ({ favorite, disabled, onToggle, className }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    const cardHover = favorite
        ? 'hover:bg-slate-grey active:bg-slate-grey-800'
        : 'hover:bg-primary active:bg-primary-800';
    const ribbonColor = favorite
        ? 'bg-slate-grey group-hover:bg-slate-grey-800'
        : 'bg-primary group-hover:bg-primary-800';

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onToggle}
            className={twMerge(
                'group relative flex w-full items-center justify-center rounded-5 border border-slate-grey-200 bg-white py-3.5',
                'shadow-[0_2px_10px_0_rgba(0,0,0,0.1)] disabled:hover:bg-white',
                // Room for the ribbon: 10px inset + 40px wide + a gap.
                'ps-4 pe-14',
                cardHover,
                className,
            )}
        >
            {/* Sits just above the card's top edge, as `top: -4px` did. */}
            <div
                className={twMerge(
                    'absolute -top-1 flex justify-center pb-3',
                    RIBBON_SHAPE,
                    ribbonColor,
                    'end-2.5 w-10',
                )}
            >
                <img src={starWhite} alt="" className="my-2 w-5" />
            </div>

            <div>
                <Text
                    as="div"
                    weight="semibold"
                    className="text-xl uppercase leading-5 text-slate-grey group-hover:text-white group-active:text-white"
                >
                    {favorite ? t('saved_to_my_courses') : t('add_to_my_courses')}
                </Text>
                {favorite && (
                    <Text
                        as="div"
                        variant="small"
                        className="normal-case group-hover:text-white group-active:text-white"
                    >
                        {t('tap_to_remove')}
                    </Text>
                )}
            </div>
        </button>
    );
};
